import {
  admissibleDomainEnds,
  normalizeCelsiusUnit,
  parseNumericInterval,
  LimitMetricSpec,
} from '../../../../../../common/thermostat-limits';
import { convertLimitTemperature } from '../../../util/convert-temp-units';
import { CurrentAttributes, MyThermostat } from '../types';

type TargetKey = 'heatSetpoint' | 'coolSetpoint' | 'setpoint';

// These selected GraphQL virtuals expose the canonical auto capability. Missing,
// contradictory or dual-target capability is not a qualified native target.
function nativeTargetMetadata(
  device: MyThermostat | undefined,
  key: TargetKey,
) {
  if (device?.source?.type !== 'Conductor' || !device.isMitsubishiThermostat)
    return;
  const single =
    device.supportsAutoMode === true &&
    device.supportsSingleSetpointAutoMode === true &&
    device.supportsDualSetpointAutoMode === false;
  const noAuto =
    device.supportsAutoMode === false &&
    device.supportsSingleSetpointAutoMode === false &&
    device.supportsDualSetpointAutoMode === false;
  if ((!single && !noAuto) || (key === 'setpoint' && !single)) return;
  const target = device.deviceMetrics?.find((entry) => entry?.key === key)
    ?.value as LimitMetricSpec | undefined;
  const parsed = parseNumericInterval(target?.value);
  if (
    !target ||
    !parsed.ok ||
    !normalizeCelsiusUnit(target.units ?? target.unit) ||
    (target.units != null &&
      target.unit != null &&
      normalizeCelsiusUnit(target.units) !==
        normalizeCelsiusUnit(target.unit)) ||
    !['number', 'decimal', 'float', 'double', 'integer', 'int'].includes(
      String(target.valueType).toLowerCase(),
    ) ||
    target.readOnly !== false ||
    typeof target.valueStep !== 'number' ||
    !Number.isFinite(target.valueStep) ||
    target.valueStep <= 0
  )
    return;
  return { interval: parsed.value, step: target.valueStep };
}

/** Native target metadata is part of edit authority, not ordinary telemetry.
 * A changed grid/range must invalidate drafts before display or Save uses it. */
export function nativeTargetCapabilitySignature(device?: MyThermostat) {
  if (device?.source?.type !== 'Conductor' || !device.isMitsubishiThermostat)
    return;
  return JSON.stringify(
    device.deviceMetrics
      ?.filter((entry) =>
        ['heatSetpoint', 'coolSetpoint', 'setpoint'].includes(entry?.key ?? ''),
      )
      .map((entry) => [
        entry?.key,
        {
          value: entry?.value?.value,
          valueStep: entry?.value?.valueStep,
          units: entry?.value?.units,
          unit: entry?.value?.unit,
          valueType: entry?.value?.valueType,
          readOnly: entry?.value?.readOnly,
        },
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  );
}

/** Preserve the source grid's visible precision only for qualified native targets. */
export function nativeTargetDigits(
  device: MyThermostat | undefined,
  key: TargetKey,
  units: 'C' | 'F',
): number | undefined {
  const target = nativeTargetMetadata(device, key);
  if (!target) return;
  const step = target.step * (units === 'F' ? 1.8 : 1);
  for (let digits = 0; digits < 12; digits++) {
    const scaled = step * 10 ** digits;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-8) return digits;
  }
  return 12;
}

/** Choose the next representable source target inside both native and policy
 * bounds. Display-unit increments cannot represent a Celsius source grid. */
export function nextNativeTarget(
  device: MyThermostat | undefined,
  values: Partial<CurrentAttributes>,
  key: TargetKey,
  delta: number,
  units: 'C' | 'F',
): number | undefined {
  const target = nativeTargetMetadata(device, key);
  const current = convertLimitTemperature(values[key], 'C', units);
  if (
    !target ||
    current === undefined ||
    !Number.isFinite(delta) ||
    delta === 0
  )
    return;
  const heat = key !== 'coolSetpoint';
  const cool = key !== 'heatSetpoint';
  const lows = [
    ...(heat ? [values.minHeatSetpointLimit] : []),
    ...(cool ? [values.minCoolSetpointLimit] : []),
  ].map((value) => convertLimitTemperature(value, 'C', units));
  const highs = [
    ...(heat ? [values.maxHeatSetpointLimit] : []),
    ...(cool ? [values.maxCoolSetpointLimit] : []),
  ].map((value) => convertLimitTemperature(value, 'C', units));
  if ([...lows, ...highs].some((value) => value === undefined)) return;
  const lower = Math.max(target.interval.lower, ...(lows as number[]));
  const upper = Math.min(target.interval.upper, ...(highs as number[]));
  if (lower > upper) return;
  const interval = {
    lower: delta > 0 ? Math.max(lower, current) : lower,
    upper: delta < 0 ? Math.min(upper, current) : upper,
    lowerInclusive:
      (lower > target.interval.lower || target.interval.lowerInclusive) &&
      !(delta > 0 && current >= lower),
    upperInclusive:
      (upper < target.interval.upper || target.interval.upperInclusive) &&
      !(delta < 0 && current <= upper),
  };
  if (interval.lower > interval.upper) return;
  const ends = admissibleDomainEnds({ interval, step: target.step });
  return convertLimitTemperature(
    delta > 0 ? ends.minimum : ends.maximum,
    units,
  );
}
